import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, Search } from 'lucide-react';
import apiClient from '../api/client';
import { useToast } from '../components/ui/Toast';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { PageHeader } from '../components/ui/PageHeader';
import { formatDate } from '../lib/format';
import { cn } from '../utils/cn';

type Status = 'PENDING' | 'TO_CHECK' | 'EAN_MISSING' | 'EAN_READY';
interface EanRow {
  id: string;
  ean: string;
  source: 'TECDOC' | 'LEARNED' | 'MANUAL';
  packQty: number;
}
interface PrepLine {
  lineId: string;
  poNumber: string;
  supplierCode: string;
  brand: string | null;
  reference: string;
  productName: string;
  outstanding: number;
  orderNumber: string | null;
  expectedAt: string;
  ready: boolean;
  resolutionId: string | null;
  status: Status | null;
  tecdocBrand: string | null;
  tecdocArticleNr: string | null;
  candidates: Array<{ brand: string; articleNr: string }>;
  lastError: string | null;
  eans: EanRow[];
}
interface Article {
  supId: number;
  brand: string;
  articleNr: string;
  eans: string[];
}

const TONE: Record<Status, string> = {
  TO_CHECK: 'bg-destructive/10 text-destructive',
  PENDING: 'bg-warning/[.13] text-warning',
  EAN_MISSING: 'bg-muted text-muted-foreground',
  EAN_READY: 'bg-success/[.12] text-success',
};

/**
 * EAN preparation (migration 138, guide §5b) — about two hours before reception, one operator.
 *
 * AutoPartner and Inter Cars send no EAN; the resolver found most of them in TecDoc when the
 * order was saved, and this lists only the rest: TO_CHECK (part not found, or several) and
 * EAN_MISSING (found, but TecDoc has no EAN). Three answers per line — pick the TecDoc article,
 * type the EAN from the box or the supplier's site (check digit verified), or leave it: at
 * reception the part is then received by its manufacturer reference. The fix is made on the PART,
 * so the list shrinks every day. "97 / 100 ready" is the KPI (target ≥ 98 %).
 */
export default function EanPrep() {
  const { t } = useTranslation();
  const { push, showApiError } = useToast();
  const [data, setData] = useState<{ total: number; ready: number; lines: PrepLine[] } | null>(
    null
  );
  const [busy, setBusy] = useState(false);

  /* Every open line, ready or not (2026-10-24): a screen that only listed the problems read as
     "nothing here" — the team could not see that the parts WERE ready, nor with which code. */
  const load = useCallback(() => {
    apiClient
      .get<{ total: number; ready: number; lines: PrepLine[] }>('/receiving/ext-ean/prep', {
        params: { all: '1' },
      })
      .then((r) => setData(r.data))
      .catch(showApiError);
  }, [showApiError]);
  const notReady = data?.lines.filter((l) => !l.ready) ?? [];
  const ready = data?.lines.filter((l) => l.ready) ?? [];
  useEffect(load, [load]);

  const run = async () => {
    setBusy(true);
    try {
      const { data: r } = await apiClient.post<{ looked: number; ready: number }>(
        '/receiving/ext-ean/run',
        {},
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      push({ tone: 'success', message: t('rcv.eanPrep.runDone', r) });
      load();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const pct = data && data.total > 0 ? Math.round((data.ready / data.total) * 1000) / 10 : 100;

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('rcv.eanPrep.title')}
        description={t('rcv.eanPrep.subtitle')}
        actions={
          <Button size="sm" variant="outline" disabled={busy} onClick={() => void run()}>
            <RefreshCw className={cn('h-4 w-4', busy && 'animate-spin')} />
            {t('rcv.eanPrep.run')}
          </Button>
        }
      />

      {data && (
        <div className="flex flex-wrap items-center gap-4">
          <div className="rounded-lg border bg-card px-4 py-2 shadow-sm">
            <div
              className={cn(
                'text-2xl font-semibold tabular-nums',
                pct >= 98 ? 'text-success' : 'text-warning'
              )}
            >
              {t('rcv.eanPrep.ready', { ready: data.ready, total: data.total })}
            </div>
            <div className="text-xs text-muted-foreground">
              {pct} % · {t('rcv.eanPrep.target')}
            </div>
          </div>
        </div>
      )}

      {data && (
        <section className="space-y-2">
          <h2 className="text-base font-semibold">
            {t('rcv.eanPrep.notReadyTitle', { count: notReady.length })}
          </h2>
          {notReady.length === 0 ? (
            <p className="rounded border bg-card p-4 text-sm text-muted-foreground">
              {t('rcv.eanPrep.none')}
            </p>
          ) : (
            <ul className="space-y-2">
              {notReady.map((l) => (
                <PrepRow key={l.lineId} line={l} onChanged={load} />
              ))}
            </ul>
          )}
        </section>
      )}

      {data && ready.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-base font-semibold">
            {t('rcv.eanPrep.readyTitle', { count: ready.length })}
          </h2>
          <ul className="space-y-2">
            {ready.map((l) => (
              <PrepRow key={l.lineId} line={l} onChanged={load} />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function PrepRow({ line, onChanged }: { line: PrepLine; onChanged: () => void }) {
  const { t } = useTranslation();
  const { push, showApiError } = useToast();
  const [mode, setMode] = useState<'none' | 'search' | 'ean'>('none');
  const [hits, setHits] = useState<Article[] | null>(null);
  const [ean, setEan] = useState('');
  const [packQty, setPackQty] = useState('1');
  const [busy, setBusy] = useState(false);
  /** A ready part can still be wrong (a mistaken automatic pick, a box with another code). */
  const [correcting, setCorrecting] = useState(false);
  const status: Status = line.status ?? 'PENDING';
  const cmd = { headers: { 'X-Command-Id': crypto.randomUUID() } };

  const search = async () => {
    setMode('search');
    setHits(null);
    try {
      const { data } = await apiClient.get<{ data: Article[] }>(
        '/receiving/ext-ean/tecdoc-search',
        {
          params: { brand: line.brand ?? '', ref: line.reference },
        }
      );
      setHits(data.data);
    } catch (err) {
      showApiError(err);
      setHits([]);
    }
  };

  const pick = async (a: Article) => {
    if (!line.resolutionId) return;
    setBusy(true);
    try {
      const { data } = await apiClient.post<{ status: Status }>(
        `/receiving/ext-ean/${line.resolutionId}/pick`,
        { supId: a.supId, articleNr: a.articleNr },
        cmd
      );
      push({
        tone: 'success',
        message: t('rcv.eanPrep.picked', { status: t(`rcv.eanPrep.status.${data.status}`) }),
      });
      setMode('none');
      setCorrecting(false);
      onChanged();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const saveEan = async () => {
    if (!line.resolutionId) return;
    setBusy(true);
    try {
      await apiClient.post(
        `/receiving/ext-ean/${line.resolutionId}/ean`,
        { ean: ean.trim(), packQty: Number(packQty) || 1 },
        cmd
      );
      push({ tone: 'success', message: t('rcv.eanPrep.saved') });
      setEan('');
      setMode('none');
      setCorrecting(false);
      onChanged();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    try {
      await apiClient.post(`/receiving/ext-ean/ean/${id}/remove`, {}, cmd);
      onChanged();
    } catch (err) {
      showApiError(err);
    }
  };

  return (
    <li className="rounded border bg-card p-3 text-sm">
      <div className="flex flex-wrap items-center gap-3">
        <span
          className={cn(
            'rounded px-2 py-0.5 text-xs font-semibold',
            line.ready ? TONE.EAN_READY : TONE[status === 'EAN_READY' ? 'PENDING' : status]
          )}
        >
          {line.ready
            ? t('rcv.eanPrep.readyBadge')
            : `${t('rcv.eanPrep.notReadyBadge')} · ${t(`rcv.eanPrep.status.${status}`)}`}
        </span>
        <span className="font-semibold">{line.brand ?? '—'}</span>
        <span className="font-mono">{line.reference}</span>
        <span className="text-muted-foreground">{line.productName}</span>
        <span className="ml-auto text-xs text-muted-foreground">
          {line.supplierCode} · {line.poNumber}
          {line.orderNumber ? ` · ${t('rcv.eanPrep.order', { n: line.orderNumber })}` : ''} ·{' '}
          {t('rcv.eanPrep.expected', { date: formatDate(line.expectedAt) })} · ×{line.outstanding}
        </span>
      </div>

      <div className="mt-1 text-xs text-muted-foreground">
        {line.tecdocArticleNr &&
          t('rcv.eanPrep.found', { brand: line.tecdocBrand, nr: line.tecdocArticleNr })}
        {status === 'TO_CHECK' &&
          line.candidates.length > 0 &&
          ' ' +
            t('rcv.eanPrep.candidates', {
              list: line.candidates.map((c) => `${c.brand} ${c.articleNr}`).join(', '),
            })}
        {line.lastError && <span className="text-destructive"> · {line.lastError}</span>}
      </div>
      {line.eans.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-2 text-xs">
          {line.eans.map((e) => (
            <span key={e.id} className="rounded bg-muted px-2 py-0.5 font-mono">
              {e.ean} · {e.source}
              {e.packQty > 1 ? ` · ×${e.packQty}` : ''}
              {e.source !== 'TECDOC' && (
                <button
                  type="button"
                  className="ml-2 text-destructive hover:underline"
                  onClick={() => void remove(e.id)}
                >
                  {t('rcv.eanPrep.remove')}
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {line.ready && line.resolutionId && !correcting && (
        <button
          type="button"
          className="mt-1 text-xs text-muted-foreground underline hover:text-foreground"
          onClick={() => setCorrecting(true)}
        >
          {t('rcv.eanPrep.correct')}
        </button>
      )}
      {(!line.ready || correcting) && line.resolutionId && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => void search()}>
            <Search className="h-4 w-4" />
            {t('rcv.eanPrep.search')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setMode(mode === 'ean' ? 'none' : 'ean')}
          >
            {t('rcv.eanPrep.addEan')}
          </Button>
          {!line.ready && (
            <span className="text-xs text-muted-foreground">{t('rcv.eanPrep.leave')}</span>
          )}
        </div>
      )}

      {mode === 'ean' && (
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <Input
            className="h-8 w-64 font-mono"
            inputMode="numeric"
            placeholder={t('rcv.eanPrep.eanPlaceholder')}
            value={ean}
            onChange={(e) => setEan(e.target.value)}
          />
          <label className="flex items-center gap-1 text-xs">
            {t('rcv.eanPrep.packQty')}
            <Input
              className="h-8 w-16"
              type="number"
              min={1}
              max={999}
              value={packQty}
              onChange={(e) => setPackQty(e.target.value)}
            />
          </label>
          <Button size="sm" disabled={busy || ean.trim().length < 8} onClick={() => void saveEan()}>
            {t('rcv.eanPrep.save')}
          </Button>
        </div>
      )}

      {mode === 'search' && (
        <div className="mt-2 rounded border">
          {hits === null && <p className="p-2 text-xs text-muted-foreground">{t('rcv.loading')}</p>}
          {hits?.length === 0 && <p className="p-2 text-xs">{t('rcv.eanPrep.noHit')}</p>}
          {hits?.map((a) => (
            <div
              key={`${a.supId}-${a.articleNr}`}
              className="flex flex-wrap items-center gap-3 border-t p-2 first:border-t-0"
            >
              <span className="font-semibold">{a.brand}</span>
              <span className="font-mono">{a.articleNr}</span>
              <span className="text-xs text-muted-foreground">
                {a.eans.length ? a.eans.join(', ') : t('rcv.eanPrep.noEans')}
              </span>
              <Button size="sm" className="ml-auto" disabled={busy} onClick={() => void pick(a)}>
                {t('rcv.eanPrep.choose')}
              </Button>
            </div>
          ))}
        </div>
      )}
    </li>
  );
}
